// Die ungelesenen Posteingangs-Mails EINZELN für das Mitteilungszentrum.
//
// Quelle: `GET /mail/inbox?folder=inbox&unreadOnly=true` je Postfach, das in
// der Glocke zählt (T-249, Standard: nur das eigene) — dieselbe Auswahl wie die
// Zahl an Glocke und „Schriftverkehr". Das Backend filtert dort schon nach
// Postfach-Zugriff und Kontakt-Sichtbarkeit (cbb@). Bis zu 200 je Postfach.
//
// Neu geladen wird, sobald sich die Live-Zahl der Ungelesenen ändert (sie kommt
// aus dem MailContext, ohne eigenen Poll) — liest jemand eine Mail, fällt sie
// so von selbst heraus.
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../utils/apiFetch";
import { useMailContext } from "../../context/MailContext";
import { useGlockenPostfaecher } from "../mail/useMailPostfachEinstellungen";
import { zaehltInGlocke } from "../mail/glockenPostfaecher";
import type { PosteingangMail } from "./clusterNotifications";

type InboxItem = {
    id: string;
    internetMessageId?: string | null;
    subject?: string | null;
    from?: { name?: string | null; address?: string | null } | null;
    receivedDateTime?: string | null;
    bodyPreview?: string | null;
    meetingMessageType?: string | null;
    isRead?: boolean;
};

export const POSTEINGANG_MAX = 200;

/** Graph-Listeneintrag → Zeile; reine Funktion (testbar). */
export function alsPosteingangMail(mailbox: string, m: InboxItem): PosteingangMail {
    return {
        mailbox,
        id: m.id,
        internetMessageId: m.internetMessageId ?? null,
        absender: m.from?.name?.trim() || m.from?.address?.trim() || "Unbekannt",
        betreff: m.subject?.trim() || "(ohne Betreff)",
        vorschau: (m.bodyPreview ?? "").replace(/\s+/g, " ").trim(),
        empfangen: m.receivedDateTime ?? "",
        terminArt: m.meetingMessageType ?? null,
    };
}

/** `aktiv = false`: nichts laden (z. B. Glocke zu). Liefert `null`, solange nichts geladen ist. */
export function usePosteingangUngelesen(aktiv: boolean): PosteingangMail[] | null {
    const { unreadByMailbox, ownMailbox } = useMailContext();
    const { wert: wahl } = useGlockenPostfaecher(aktiv);
    const [mails, setMails] = useState<PosteingangMail[] | null>(null);

    // Nur Postfächer, die zählen UND Ungelesene haben; der Schlüssel enthält die
    // Zahl, damit jede Änderung (gelesen, neu) einen Abruf auslöst.
    const ziele = useMemo(
        () =>
            Object.entries(unreadByMailbox)
                .filter(([mb, n]) => n > 0 && zaehltInGlocke(mb, wahl, ownMailbox))
                .sort(([a], [b]) => a.localeCompare(b)),
        [unreadByMailbox, wahl, ownMailbox],
    );
    const schluessel = ziele.map(([mb, n]) => `${mb}:${n}`).join("|");

    useEffect(() => {
        if (!aktiv) return;
        if (!ziele.length) {
            setMails([]);
            return;
        }
        const ctrl = new AbortController();
        Promise.allSettled(
            ziele.map(([mailbox]) =>
                apiFetch<InboxItem[]>("mail/inbox", {
                    signal: ctrl.signal,
                    params: { mailbox, folder: "inbox", unreadOnly: true, top: POSTEINGANG_MAX, mitTerminArt: true },
                }).then(({ data }) =>
                    (Array.isArray(data) ? data : []).filter((m) => m && m.id && m.isRead !== true).map((m) => alsPosteingangMail(mailbox, m)),
                ),
            ),
        ).then((ergebnisse) => {
            if (ctrl.signal.aborted) return;
            setMails(ergebnisse.flatMap((e) => (e.status === "fulfilled" ? e.value : [])));
        });
        return () => ctrl.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aktiv, schluessel]);

    return aktiv ? mails : null;
}
